import firebase from 'firebase/app'
import 'firebase/auth'
import axios from 'axios'

// Report pages open from emailed links with no user session. Instead of signing
// in as the shared `happily@happily.ai` account (whose password shipped in the
// bundle), we exchange the opaque token already in the page URL for a short-lived
// Firebase custom token, server-side. The token/type are read straight from the
// URL so callers only need `await ensureReportAuth()` — no threading required.
const EXCHANGE_URL = 'https://us-central1-people2-admin.cloudfunctions.net/exchangeReportToken'

// Map the first path segment to a report type + where its token lives in the URL.
// Keep in sync with REPORT_TYPES in happily-functions insightsReportApi.js.
function currentReportContext(): { type: string; token: string } | null {
  const path = window.location.pathname
  const segments = path.split('/').filter(Boolean)
  const first = segments[0] || ''
  const idParam = segments[1] ? decodeURIComponent(segments[1]) : ''
  const uid = new URLSearchParams(window.location.search).get('uid') || ''

  switch (first) {
    case 'monthly-insights': return { type: 'monthly-insights', token: uid }
    case 'quarterly-insights': return { type: 'quarterly-insights', token: uid }
    case 'company-health-status': return { type: 'company-health-status', token: idParam }
    case 'high-performing-managers': return { type: 'high-performing-managers', token: idParam }
    case 'performance-summary-report': return { type: 'performance-summary-report', token: idParam }
    case 'daily-insights-detail': return { type: 'daily-insights-detail', token: idParam }
    case 'daily-insights': return { type: 'daily-insights', token: idParam }
    case 'weekly-insights': return { type: 'weekly-insights', token: idParam }
    case 'peerfeedback': return { type: 'peer-feedback', token: idParam }
    default: return null
  }
}

let inflight: Promise<any> | null = null
let authenticatedContextKey = ''

function reportContextKey(ctx: { type: string; token: string }): string {
  return `${ctx.type}:${ctx.token}`
}

// Resolves once Firebase has loaded its persisted session (or confirmed there
// is none). Before this, currentUser is always null even for returning
// visitors, which would trigger a needless token exchange on every load.
let authReadyPromise: Promise<void> | null = null
function authReady(): Promise<void> {
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsubscribe = firebase.auth().onAuthStateChanged(() => {
        unsubscribe()
        resolve()
      })
    })
  }
  return authReadyPromise
}

// Ensure a Firebase session exists for the current report page. Safe to call
// from multiple store actions concurrently — the exchange runs at most once.
export async function ensureReportAuth(): Promise<any> {
  await authReady()
  const ctx = currentReportContext()
  if (!ctx || !ctx.token) {
    throw new Error('ensureReportAuth: no report token in URL')
  }

  const contextKey = reportContextKey(ctx)
  const current = firebase.auth().currentUser
  if (current && authenticatedContextKey === contextKey) {
    return current
  }
  if (inflight) {
    return inflight
  }

  inflight = axios.post(EXCHANGE_URL, ctx, { timeout: 20000 })
    .then((res) => {
      const customToken = res.data && res.data.customToken
      if (!customToken) {
        throw new Error('ensureReportAuth: no customToken returned')
      }
      return firebase.auth().signInWithCustomToken(customToken)
    })
    .then((credential) => {
      authenticatedContextKey = contextKey
      return credential
    })
    .finally(() => { inflight = null })

  return inflight
}
